// ComposedExperienceHyperpop — Tier-A "hyperpop" skin for composed experiences.
//
// A per-world treatment of the composed runtime (sibling to ComposedExperienceAurora
// and ComposedExperienceObsidian), proving the dispatcher pattern generalises. Same
// data + capture contract as ComposedExperienceSlide (both consume useComposedExperience),
// rendered in the HYPERPOP ARCADE visual language shared with HyperpopDragSort: a
// near-black stage, electric candy pops, thick borders, hard offset sticker shadows,
// chunky uppercase type, and — the standout — a re-rate rate-slider that snaps a BOLD
// candy SHIFT METER from the learner's first reading to their revised one, popping a
// sticker into place on commit, so a change of mind is made visible (§8.12b: juice =
// cognition, not clicks). Hyperpop's signature is the shift-meter/pop-in, where Aurora's
// was the ribbon/bloom and Obsidian's the red string.

import { useEffect, useMemo, useState } from 'react';
import type { Theme } from '../../slideTypes';
import type { ComposedExperienceSlideData } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import type {
  ChoiceBlock,
  CommitBoxBlock,
  CompositionBlock,
  ConfidenceBlock,
  FlagInTextBlock,
  PerspectiveLensBlock,
  RateSliderBlock,
  RevealBlock,
  SelectEvidenceBlock,
  SortZonesBlock,
  StimulusBlock,
} from '../../../../../lib/scenario-builder/composition/types';
import type { Answers, BlockAnswer } from '../../../../../lib/scenario-builder/composition/capture';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import {
  useThemeWorldAudio,
  playHyperpopWin,
  playHyperpopLose,
  playHyperpopLand,
  playHyperpopTick,
} from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';
import { Typewriter } from '../../../../../lib/scenario-themes/Typewriter';
import { useComposedExperience } from './useComposedExperience';

interface Props {
  data: ComposedExperienceSlideData;
  theme: Theme;
  isEditing?: boolean;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

// Hyperpop palette (mockup-faithful constants, matching HyperpopDragSort).
const BLUE = '#2d5cff';
const PINK = '#ff3d9a';
const YELLOW = '#ffe600';
const LIME = '#3ddc84';
const TANGERINE = '#ff6b1a';
const INK = '#0a0a0a';
const PAPER = '#16121e';
const NEARWHITE = '#f6f6ff';

const STYLE_ID = 'hyperpop-composed-scoped-styles';

// A cue runner bound to the hyperpop engine (from useThemeWorldAudio.playWith).
type PlayWith = ReturnType<typeof useThemeWorldAudio>['playWith'];
// The named-cue runner ('select', 'commit', …) from the same hook.
type Play = ReturnType<typeof useThemeWorldAudio>['play'];

function scopedCss(): string {
  return `
.hypcx {
  position: relative;
  --ink: ${INK};
  --spring: cubic-bezier(.34,1.56,.64,1);
  --spring-big: cubic-bezier(.18,1.85,.5,1);
}
.hypcx * { box-sizing: border-box; }

.hypcx .hyp-glass {
  background: ${PAPER}; border: 3px solid ${NEARWHITE}; border-radius: 18px;
  box-shadow: 6px 6px 0 ${BLUE}; position: relative; padding: 22px 24px;
}
.hypcx .hyp-kicker {
  display: inline-block; font-size: 0.78rem; letter-spacing: 0.12em; text-transform: uppercase; font-weight: 800;
  color: #fff; background: ${PINK}; border: 3px solid ${NEARWHITE}; border-radius: 999px;
  padding: 4px 14px; margin-bottom: 12px; box-shadow: 3px 3px 0 ${YELLOW}; transform: rotate(-2deg);
}
.hypcx .hyp-prompt { font-family: inherit; font-size: 1.06rem; font-weight: 700; color: ${NEARWHITE}; margin: 0 0 6px; line-height: 1.34; }
.hypcx .hyp-body { color: ${NEARWHITE}; font-size: 1rem; line-height: 1.6; margin: 0; }
.hypcx .hyp-quote { border-left: 5px solid ${YELLOW}; padding: 6px 0 6px 16px; color: ${NEARWHITE}; font-weight: 600; }

.hypcx textarea.hyp-input {
  width: 100%; font-family: inherit; font-size: 1rem; font-weight: 600; color: ${NEARWHITE}; line-height: 1.5;
  background: ${INK}; border: 3px solid ${NEARWHITE}; border-radius: 12px; padding: 12px 14px; resize: vertical;
  transition: border-color .18s, box-shadow .18s;
}
.hypcx textarea.hyp-input::placeholder { color: rgba(246,246,255,0.4); }
.hypcx textarea.hyp-input:focus { outline: none; border-color: ${LIME}; box-shadow: 4px 4px 0 ${LIME}; }

/* gauge */
.hypcx .hyp-scale-val { font-size: 3rem; font-weight: 800; line-height: 1; color: ${YELLOW}; -webkit-text-stroke: 1.5px ${INK}; text-align: center; letter-spacing: 0.02em; }
.hypcx input[type="range"].hyp-range { -webkit-appearance: none; appearance: none; width: 100%; height: 10px; border-radius: 999px; outline: none; cursor: grab; border: 2px solid ${NEARWHITE}; background: ${INK}; }
.hypcx input[type="range"].hyp-range:disabled { cursor: default; }
.hypcx input[type="range"].hyp-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 24px; height: 24px; border-radius: 8px; background: ${LIME}; border: 3px solid ${NEARWHITE}; box-shadow: 3px 3px 0 ${INK}; }
.hypcx input[type="range"].hyp-range::-moz-range-thumb { width: 24px; height: 24px; border-radius: 8px; border: 3px solid ${NEARWHITE}; background: ${LIME}; box-shadow: 3px 3px 0 ${INK}; }

/* the change-of-mind SHIFT METER (re-rate standout) */
.hypcx .hyp-track { position: relative; height: 48px; margin: 16px 0 6px; }
.hypcx .hyp-rail { position: absolute; top: 50%; left: 0; right: 0; height: 10px; transform: translateY(-50%); border-radius: 999px; background: ${INK}; border: 2px solid ${NEARWHITE}; }
.hypcx .hyp-range.hyp-overlay { position: absolute; inset: 0; height: 100%; background: transparent; border: none; z-index: 4; }
.hypcx .hyp-ghost { position: absolute; top: 50%; width: 20px; height: 20px; border-radius: 6px; transform: translate(-50%,-50%) rotate(45deg); border: 3px solid ${NEARWHITE}; background: ${INK}; box-shadow: 2px 2px 0 ${TANGERINE}; z-index: 3; }
.hypcx .hyp-shift {
  position: absolute; top: 50%; height: 14px; transform: translateY(-50%); border-radius: 999px; z-index: 2;
  background: linear-gradient(90deg, ${BLUE}, ${PINK}, ${YELLOW}, ${LIME});
  border: 2px solid ${NEARWHITE}; box-shadow: 3px 3px 0 ${INK};
  transition: left .5s var(--spring), width .5s var(--spring);
}
.hypcx .hyp-shift.snap { animation: hypSnap .5s var(--spring-big); }
@keyframes hypSnap { 0% { transform: translateY(-50%) scale(.6); } 55% { transform: translateY(-50%) scaleY(1.55) scaleX(1.05); } 100% { transform: translateY(-50%) scale(1); } }
.hypcx .hyp-delta {
  display: inline-block; font-size: 0.9rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: ${INK};
  background: ${YELLOW}; border: 3px solid ${NEARWHITE}; border-radius: 12px; padding: 6px 14px; margin-top: 12px; box-shadow: 3px 3px 0 ${PINK}; transform: rotate(-1.5deg);
}
.hypcx .hyp-ends { display: flex; justify-content: space-between; font-size: 0.74rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: rgba(246,246,255,0.65); margin-top: 4px; }

.hypcx .hyp-btn {
  margin-top: 16px; font-size: 0.95rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: ${INK}; cursor: pointer;
  padding: 12px 26px; border-radius: 14px; border: 3px solid ${NEARWHITE}; background: ${LIME}; -webkit-text-stroke: 0.4px ${INK};
  box-shadow: 5px 5px 0 ${PINK}; transition: transform .13s var(--spring), box-shadow .13s var(--spring), background .2s, opacity .2s;
}
.hypcx .hyp-btn:hover:not(:disabled) { transform: translate(-2px,-2px); box-shadow: 7px 7px 0 ${PINK}; background: ${YELLOW}; }
.hypcx .hyp-btn:active:not(:disabled) { transform: translate(2px,2px); box-shadow: 2px 2px 0 ${PINK}; }
.hypcx .hyp-btn:disabled { opacity: 0.4; filter: grayscale(.4); cursor: not-allowed; box-shadow: 3px 3px 0 ${INK}; }

/* options */
.hypcx .hyp-opt {
  width: 100%; text-align: left; font-weight: 700; color: ${NEARWHITE}; background: ${INK}; border: 3px solid ${NEARWHITE};
  border-radius: 12px; padding: 12px 14px; font-family: inherit; font-size: 1rem; cursor: pointer; box-shadow: 3px 3px 0 rgba(45,92,255,0.9);
  transition: transform .12s var(--spring), box-shadow .12s var(--spring), background .2s, color .2s;
}
.hypcx .hyp-opt:hover:not(:disabled) { transform: translate(-2px,-2px); box-shadow: 5px 5px 0 ${BLUE}; }
.hypcx .hyp-opt.hyp-on { background: ${LIME}; color: ${INK}; box-shadow: 4px 4px 0 ${INK}; }
.hypcx .hyp-opt:disabled { cursor: default; }

/* reveal — bursts open with a sticker POP-IN once its ref commits */
.hypcx .hyp-sealed {
  border: 3px dashed ${PINK}; border-radius: 16px; padding: 20px; text-align: center; color: rgba(246,246,255,0.72);
  font-size: 0.86rem; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; background: rgba(255,61,154,0.08);
}
.hypcx .hyp-reveal { opacity: 0; transform: scale(0.6); animation: hypPop .5s var(--spring-big) forwards; overflow: hidden; }
@keyframes hypPop { 0% { opacity: 0; transform: scale(0.6); } 60% { opacity: 1; transform: scale(1.08); } 100% { opacity: 1; transform: scale(1); } }

/* ending */
.hypcx .hyp-settle { text-align: center; margin-bottom: 16px; }
.hypcx .hyp-settle span {
  display: inline-block; font-size: 1.3rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #fff;
  background: ${PINK}; -webkit-text-stroke: 1px ${INK}; border: 3px solid ${NEARWHITE}; border-radius: 16px; padding: 8px 22px;
  box-shadow: 6px 6px 0 ${YELLOW}; transform: rotate(-2deg);
}
.hypcx .hyp-settle .hyp-arc { height: 4px; width: 130px; margin: 16px auto 0; border-radius: 999px; background: linear-gradient(90deg, ${BLUE}, ${PINK}, ${YELLOW}, ${LIME}); border: 1px solid ${INK}; }

@media (prefers-reduced-motion: reduce) {
  .hypcx * { animation: none !important; }
  .hypcx .hyp-shift, .hypcx .hyp-reveal { transition: none !important; }
  .hypcx .hyp-reveal { opacity: 1; transform: none; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    tag.dataset.refcount = String(Number(tag.dataset.refcount || '0') + 1);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────

export function ComposedExperienceHyperpop({
  data,
  theme,
  isEditing,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  useScopedStyles();

  const { blocks, answers, setAnswer, complete, values, rollup } = useComposedExperience({
    data,
    isEditing,
    onActivityResponse,
    onComplete,
    savedResponse,
  });

  const { enabled: audioOn, toggle: toggleAudio, play, playWith } = useThemeWorldAudio('hyperpop');

  return (
    <div
      className="hypcx h-full w-full overflow-y-auto"
      style={{
        background: `radial-gradient(1100px 640px at 50% -5%, rgba(45,92,255,0.22), transparent 60%), radial-gradient(900px 600px at 100% 20%, rgba(255,61,154,0.2), transparent 55%), ${INK}`,
        color: NEARWHITE,
        fontFamily: theme.fonts.body,
      }}
    >
      <ThemeWorldAtmosphere worldId="hyperpop" primary={BLUE} accent={PINK} background={INK} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 10 }}>
        <div className="mx-auto" style={{ maxWidth: 720, padding: '56px 24px 110px' }}>
          <header style={{ marginBottom: 30 }}>
            <div style={{ fontFamily: theme.fonts.code, fontSize: 12, fontWeight: 800, letterSpacing: '0.24em', color: YELLOW, textTransform: 'uppercase', marginBottom: 14 }}>
              ★ Gut check ★
            </div>
            <h1 style={{ margin: 0 }}>
              <Typewriter
                text={data.title}
                speed={30}
                caretColor={YELLOW}
                style={{
                  fontFamily: theme.fonts.heading,
                  fontWeight: 800,
                  fontSize: 'clamp(1.8rem, 4.8vw, 2.9rem)',
                  lineHeight: 1.02,
                  color: NEARWHITE,
                  textTransform: 'uppercase',
                  display: 'inline-block',
                }}
              />
            </h1>
            {data.subtitle && <p style={{ color: 'rgba(246,246,255,0.72)', fontWeight: 600, fontSize: 15, margin: '12px 0 0' }}>{data.subtitle}</p>}
          </header>

          <div className="space-y-5">
            {blocks.map((block) => (
              <HypBlock
                key={block.id}
                block={block}
                answers={answers}
                answer={answers[block.id]}
                onAnswer={(a) => setAnswer(block.id, a)}
                play={play}
                playWith={playWith}
              />
            ))}
          </div>

          {complete && data.conclusion && (
            <div className="mt-10">
              <div className="hyp-settle">
                <span>Nailed it</span>
                <div className="hyp-arc" />
              </div>
              <ExperienceClose
                conclusion={data.conclusion}
                theme={theme}
                values={values}
                outcome={data.scoring.mode === 'none' ? undefined : String(Math.round(rollup * 100))}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface HypBlockProps {
  block: CompositionBlock;
  answers: Answers;
  answer: BlockAnswer | undefined;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  playWith: PlayWith;
}

function HypBlock({ block, answers, answer, onAnswer, play, playWith }: HypBlockProps) {
  switch (block.type) {
    case 'stimulus':
      return <HypStimulus block={block} />;
    case 'rate-slider':
      return <HypRateSlider block={block} answers={answers} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'confidence':
      return <HypRateSlider block={block as unknown as RateSliderBlock} answers={answers} answer={answer} onAnswer={onAnswer} playWith={playWith} confidence />;
    case 'reveal':
      return <HypReveal block={block} answers={answers} playWith={playWith} />;
    case 'commit-box':
      return <HypCommitBox block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'choice':
      return <HypChoice block={block} answer={answer} onAnswer={onAnswer} play={play} />;
    case 'select-evidence':
      return <HypSelectEvidence block={block} answer={answer} onAnswer={onAnswer} play={play} />;
    case 'sort-zones':
      return <HypSortZones block={block} answer={answer} onAnswer={onAnswer} play={play} />;
    case 'flag-in-text':
      return <HypFlagInText block={block} answer={answer} onAnswer={onAnswer} play={play} />;
    case 'perspective-lens':
      return <HypPerspective block={block} answer={answer} onAnswer={onAnswer} />;
    default:
      return null;
  }
}

function Sticker({ children }: { children: React.ReactNode }) {
  return <div className="hyp-glass">{children}</div>;
}

// ── stimulus ──────────────────────────────────────────────────────────────────
function HypStimulus({ block }: { block: StimulusBlock }) {
  return (
    <Sticker>
      {block.content.heading && <div className="hyp-kicker">{block.content.heading}</div>}
      <p className="hyp-body">{block.content.body}</p>
    </Sticker>
  );
}

// ── rate-slider (+ confidence) — the change-of-mind SHIFT METER standout ───────
function HypRateSlider({
  block,
  answers,
  answer,
  onAnswer,
  playWith,
  confidence,
}: {
  block: RateSliderBlock;
  answers: Answers;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
  confidence?: boolean;
}) {
  const { min, max, step } = block.structure;
  const committed = (answer as { value: number } | undefined)?.value;
  const [pos, setPos] = useState<number>(committed ?? Math.round((min + max) / 2));
  const locked = committed !== undefined;
  const value = locked ? committed : pos;
  const prior = block.ref ? (answers[block.ref] as { value?: number } | undefined)?.value : undefined;
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const delta = prior !== undefined ? value - prior : undefined;

  return (
    <Sticker>
      <div className="hyp-kicker">{confidence ? 'How sure?' : prior !== undefined ? 'Where you land now' : 'Your call'}</div>
      <p className="hyp-prompt">{block.content.prompt}</p>
      <div className="hyp-scale-val">{value}</div>
      <div className="hyp-track">
        <div className="hyp-rail" />
        {prior !== undefined && (
          <span
            className={`hyp-shift${locked ? ' snap' : ''}`}
            style={{ left: `${Math.min(pct(prior), pct(value))}%`, width: `${Math.abs(pct(value) - pct(prior))}%` }}
            aria-hidden
          />
        )}
        {prior !== undefined && <span className="hyp-ghost" style={{ left: `${pct(prior)}%` }} aria-hidden />}
        <input
          type="range"
          className="hyp-range hyp-overlay"
          min={min}
          max={max}
          step={step ?? 1}
          value={value}
          disabled={locked}
          onChange={(e) => {
            const v = Number(e.target.value);
            setPos(v);
            playWith(playHyperpopTick);
          }}
        />
      </div>
      <div className="hyp-ends">
        <span>{block.content.minLabel ?? min}</span>
        <span>{block.content.maxLabel ?? max}</span>
      </div>
      {prior !== undefined && delta !== undefined && (
        <div>
          <span className="hyp-delta">
            {delta === 0 ? `Held at ${value}` : `Moved ${delta > 0 ? '+' : '−'}${Math.abs(delta)} · ${prior}→${value}`}
          </span>
        </div>
      )}
      {!locked && (
        <button
          className="hyp-btn"
          onClick={() => {
            playWith(prior !== undefined ? playHyperpopWin : playHyperpopLand);
            onAnswer({ value: pos });
          }}
        >
          {prior !== undefined ? 'Lock the shift' : 'Lock it in'}
        </button>
      )}
    </Sticker>
  );
}

// ── reveal — bursts open with a POP-IN once its ref commits ────────────────────
function HypReveal({ block, answers, playWith }: { block: RevealBlock; answers: Answers; playWith: PlayWith }) {
  const unlocked = answers[block.ref] !== undefined;
  useEffect(() => {
    if (unlocked) playWith(playHyperpopWin);
  }, [unlocked, playWith]);
  if (!unlocked) {
    return <div className="hyp-sealed">🔒 Lock your call to crack this open</div>;
  }
  return (
    <div className="hyp-glass hyp-reveal">
      <div className="hyp-kicker">{block.content.heading || 'The bigger picture'}</div>
      <p className="hyp-body">{block.content.body}</p>
    </div>
  );
}

// ── commit-box — held, then set into a chunky quote ───────────────────────────
function HypCommitBox({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: CommitBoxBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { text: string } | undefined)?.text;
  const [text, setText] = useState(committed ?? '');
  const locked = committed !== undefined;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const ok = words >= (block.structure.minWords ?? 1);
  return (
    <Sticker>
      <div className="hyp-kicker">Your take</div>
      <p className="hyp-prompt">{block.content.prompt}</p>
      {locked ? (
        <div className="hyp-quote">{committed}</div>
      ) : (
        <>
          <textarea className="hyp-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={block.content.placeholder} />
          <button
            className="hyp-btn"
            disabled={!ok}
            onClick={() => {
              playWith(playHyperpopLand);
              onAnswer({ text: text.trim() });
            }}
          >
            Stick it
          </button>
        </>
      )}
    </Sticker>
  );
}

// ── choice ────────────────────────────────────────────────────────────────────
function HypChoice({
  block,
  answer,
  onAnswer,
  play,
}: {
  block: ChoiceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
}) {
  const chosen = (answer as { optionId: string } | undefined)?.optionId;
  const chosenOpt = block.content.options.find((o) => o.id === chosen);
  return (
    <Sticker>
      <p className="hyp-prompt">{block.content.prompt}</p>
      <div className="space-y-2">
        {block.content.options.map((opt) => (
          <button
            key={opt.id}
            disabled={!!chosen}
            onClick={() => {
              play('select');
              onAnswer({ optionId: opt.id });
            }}
            className={`hyp-opt${opt.id === chosen ? ' hyp-on' : ''}`}
            style={{ opacity: chosen && opt.id !== chosen ? 0.5 : 1 }}
          >
            {opt.text}
          </button>
        ))}
      </div>
      {chosenOpt?.feedback && <p style={{ marginTop: 12, fontSize: '0.92rem', fontWeight: 600, color: 'rgba(246,246,255,0.75)', fontStyle: 'italic' }}>{chosenOpt.feedback}</p>}
    </Sticker>
  );
}

// ── select-evidence — candy selections ────────────────────────────────────────
function HypSelectEvidence({
  block,
  answer,
  onAnswer,
  play,
}: {
  block: SelectEvidenceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
}) {
  const committed = (answer as { selected: string[] } | undefined)?.selected;
  const [selected, setSelected] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const max = block.structure.maxSelections;
  const atMax = max !== undefined && selected.length >= max;
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : atMax ? s : [...s, id]));
  return (
    <Sticker>
      {block.content.claim && <div className="hyp-quote" style={{ marginBottom: 12 }}>{block.content.claim}</div>}
      <p className="hyp-prompt">{block.content.prompt}</p>
      <div className="space-y-2">
        {block.content.items.map((it) => (
          <button
            key={it.id}
            disabled={locked || (!selected.includes(it.id) && atMax)}
            onClick={() => {
              play('select');
              toggle(it.id);
            }}
            className={`hyp-opt${selected.includes(it.id) ? ' hyp-on' : ''}`}
          >
            {it.text}
          </button>
        ))}
      </div>
      {max !== undefined && !locked && <p style={{ marginTop: 8, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'rgba(246,246,255,0.65)' }}>{selected.length}/{max} chosen</p>}
      {!locked && (
        <button className="hyp-btn" disabled={selected.length === 0} onClick={() => onAnswer({ selected })}>
          Lock these in
        </button>
      )}
    </Sticker>
  );
}

// ── sort-zones ────────────────────────────────────────────────────────────────
function HypSortZones({
  block,
  answer,
  onAnswer,
  play,
}: {
  block: SortZonesBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
}) {
  const committed = (answer as { placement: Record<string, string> } | undefined)?.placement;
  const [placement, setPlacement] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allPlaced = block.content.items.every((it) => placement[it.id]);
  return (
    <Sticker>
      <p className="hyp-prompt">{block.content.prompt}</p>
      <div className="space-y-3">
        {block.content.items.map((it) => (
          <div key={it.id}>
            <p style={{ fontSize: '0.94rem', fontWeight: 700, color: NEARWHITE, margin: '0 0 6px' }}>{it.text}</p>
            <div className="flex flex-wrap gap-2">
              {block.content.zones.map((z) => (
                <button
                  key={z.id}
                  disabled={locked}
                  onClick={() => {
                    play('select');
                    setPlacement((p) => ({ ...p, [it.id]: z.id }));
                  }}
                  className={`hyp-opt${placement[it.id] === z.id ? ' hyp-on' : ''}`}
                  style={{ width: 'auto', padding: '7px 14px', fontSize: '0.84rem', borderRadius: 999 }}
                >
                  {z.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {!locked && (
        <button className="hyp-btn" disabled={!allPlaced} onClick={() => onAnswer({ placement })}>
          Lock the sort
        </button>
      )}
    </Sticker>
  );
}

// ── flag-in-text ──────────────────────────────────────────────────────────────
function HypFlagInText({
  block,
  answer,
  onAnswer,
  play,
}: {
  block: FlagInTextBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
}) {
  const committed = (answer as { flagged: string[] } | undefined)?.flagged;
  const [flagged, setFlagged] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const toggle = (id: string) => setFlagged((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  return (
    <Sticker>
      <p className="hyp-prompt">{block.content.prompt}</p>
      <p className="hyp-body" style={{ lineHeight: 1.9 }}>
        {block.content.segments.map((seg) => {
          const on = flagged.includes(seg.id);
          const wasRight = locked && seg.flagged === true;
          const wasWrong = locked && on && seg.flagged !== true;
          return (
            <span
              key={seg.id}
              onClick={() => {
                if (locked) return;
                play('select');
                toggle(seg.id);
              }}
              style={{
                cursor: locked ? 'default' : 'pointer',
                background: on ? (wasWrong ? 'rgba(255,61,154,0.3)' : 'rgba(61,220,132,0.3)') : 'transparent',
                borderBottom: locked
                  ? `3px solid ${wasRight ? LIME : wasWrong ? PINK : 'transparent'}`
                  : on
                    ? `3px solid ${LIME}`
                    : '3px solid transparent',
                borderRadius: 3,
                padding: '0 2px',
              }}
            >
              {seg.text}{' '}
            </span>
          );
        })}
      </p>
      {!locked && (
        <button className="hyp-btn" disabled={flagged.length === 0} onClick={() => onAnswer({ flagged })}>
          Flag {flagged.length} spot{flagged.length === 1 ? '' : 's'}
        </button>
      )}
    </Sticker>
  );
}

// ── perspective-lens ──────────────────────────────────────────────────────────
function HypPerspective({
  block,
  answer,
  onAnswer,
}: {
  block: PerspectiveLensBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
}) {
  const committed = (answer as { takes: Record<string, string> } | undefined)?.takes;
  const [takes, setTakes] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allFilled = block.content.lenses.every((l) => (takes[l.id] ?? '').trim().length > 0);
  return (
    <Sticker>
      <p className="hyp-prompt">{block.content.prompt}</p>
      <div className="space-y-3">
        {block.content.lenses.map((lens) => (
          <div key={lens.id}>
            <p style={{ fontSize: '0.82rem', fontWeight: 800, textTransform: 'uppercase', color: YELLOW, letterSpacing: '0.08em', margin: '0 0 3px' }}>{lens.label}</p>
            {lens.hint && <p style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(246,246,255,0.65)', margin: '0 0 4px' }}>{lens.hint}</p>}
            {locked ? (
              <div className="hyp-quote">{takes[lens.id]}</div>
            ) : (
              <textarea
                className="hyp-input"
                rows={2}
                value={takes[lens.id] ?? ''}
                onChange={(e) => setTakes((tk) => ({ ...tk, [lens.id]: e.target.value }))}
              />
            )}
          </div>
        ))}
      </div>
      {!locked && (
        <button className="hyp-btn" disabled={!allFilled} onClick={() => onAnswer({ takes })}>
          Lock these in
        </button>
      )}
    </Sticker>
  );
}
